import { formatNumber } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useEffect, useId } from 'react';
import {
  badgeCountOf,
  moduleBadgeCountOf,
  moduleOfPath,
  type NavBadgeCounts,
  type NavEntry,
  type NavItem,
  type NavModule,
  type NavPage,
} from './navigation.ts';
import { useShell } from './shell.store.ts';
import styles from './Shell.module.css';

type SidebarNavProps = {
  readonly entries: readonly NavEntry[];
  readonly pinned: readonly NavItem[];
  readonly labelled: boolean;
  readonly badges: NavBadgeCounts;
};

type NavItemLinkProps = { item: NavItem; badges: NavBadgeCounts; labelKey?: string };

const NavCount = ({ count }: { count: number }) => {
  const { i18n } = useTranslation();
  return count > 0 ? (
    <span className={styles['navBadge']} aria-hidden="true">
      {formatNumber(count, i18n.language)}
    </span>
  ) : null;
};

const useBadgeLabel = (target: NavPage, badges: NavBadgeCounts): string => {
  const { t } = useTranslation();
  const label = t(target.labelKey);
  const count = badgeCountOf(target, badges);
  return target.badge !== undefined && count > 0 ? t(`shell.badge_${target.badge}`, { label, count }) : label;
};

export const NavItemLink = ({ item, badges, labelKey = item.labelKey }: NavItemLinkProps) => {
  const { t } = useTranslation();
  const accessibleLabel = useBadgeLabel(item, badges);
  return (
    <Link
      to={item.to}
      className={styles['navLink']}
      activeOptions={{ exact: item.to === '/' }}
      aria-label={accessibleLabel}
    >
      <item.icon size={20} aria-hidden="true" className={styles['navIcon']} />
      <span className={styles['navLabel']}>{t(labelKey)}</span>
      <NavCount count={badgeCountOf(item, badges)} />
    </Link>
  );
};

const ModulePageLink = ({
  page,
  pages,
  badges,
}: {
  page: NavPage;
  pages: readonly NavPage[];
  badges: NavBadgeCounts;
}) => {
  const { t } = useTranslation();
  const accessibleLabel = useBadgeLabel(page, badges);
  return (
    <Link
      to={page.to}
      className={styles['subLink']}
      activeOptions={{ exact: hasDeeperSibling(page, pages) }}
      aria-label={accessibleLabel}
    >
      {t(page.labelKey)}
      <NavCount count={badgeCountOf(page, badges)} />
    </Link>
  );
};

const hasDeeperSibling = (page: NavPage, pages: readonly NavPage[]): boolean =>
  pages.some((other) => other !== page && other.to.startsWith(`${page.to}/`));

type ModuleProps = { module: NavModule; active: boolean; badges: NavBadgeCounts };

const ModuleGroup = ({ module, active, badges }: ModuleProps) => {
  const { t } = useTranslation();
  const listId = useId();
  const open = useShell((state) => state.openModules.includes(module.id));
  const toggleModule = useShell((state) => state.toggleModule);
  return (
    <div className={styles['module']} data-active={active}>
      <button
        type="button"
        className={styles['navLink']}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => {
          toggleModule(module.id);
        }}
      >
        <module.icon size={20} aria-hidden="true" className={styles['navIcon']} />
        <span className={styles['navLabel']}>{t(module.labelKey)}</span>
        {open ? null : <NavCount count={moduleBadgeCountOf(module, badges)} />}
        <ChevronRight size={16} aria-hidden="true" className={styles['chevron']} />
      </button>
      {open ? (
        <div id={listId} className={styles['subnav']}>
          {module.pages.map((page) => (
            <ModulePageLink key={page.id} page={page} pages={module.pages} badges={badges} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const ModuleShortcut = ({ module, active, badges }: ModuleProps) => {
  const { t } = useTranslation();
  const first = module.pages[0];
  if (!first) return null;
  return (
    <Link
      to={first.to}
      className={styles['navLink']}
      data-status={active ? 'active' : undefined}
      aria-label={t(module.labelKey)}
    >
      <module.icon size={20} aria-hidden="true" className={styles['navIcon']} />
      <NavCount count={moduleBadgeCountOf(module, badges)} />
    </Link>
  );
};

export const SidebarNav = ({ entries, pinned, labelled, badges }: SidebarNavProps) => {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const activeModule = moduleOfPath(entries, pathname);
  const openModule = useShell((state) => state.openModule);

  useEffect(() => {
    if (activeModule) openModule(activeModule);
  }, [activeModule, openModule]);

  return (
    <>
      <div className={styles['navList']}>
        {entries.map((entry) => {
          if (entry.kind === 'item')
            return <NavItemLink key={entry.item.id} item={entry.item} badges={badges} />;
          const active = entry.module.id === activeModule;
          return labelled ? (
            <ModuleGroup key={entry.module.id} module={entry.module} active={active} badges={badges} />
          ) : (
            <ModuleShortcut key={entry.module.id} module={entry.module} active={active} badges={badges} />
          );
        })}
      </div>
      <div className={styles['pinnedGroup']}>
        {pinned.map((item) => (
          <NavItemLink key={item.id} item={item} badges={badges} />
        ))}
      </div>
    </>
  );
};
